/**
 * EntityParameterPanel
 *
 * Renders parameter controls from a ParameterDefinition schema.
 * This enables data-driven UI rendering - the model definition entity
 * specifies what parameters it supports and how they should be rendered,
 * and this component renders the appropriate controls.
 *
 * Features:
 * - Renders controls based on parameter type and control hint
 * - Handles conditional visibility (dependsOn)
 * - Groups parameters by category
 * - Provides defaults reset functionality
 */

import React, { useMemo, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ParameterDefinition,
  ParameterCategory,
} from '@/lib/api';

/**
 * Checks if a parameter should be visible based on its dependencies
 */
function isParameterVisible(
  definition: ParameterDefinition,
  values: Record<string, unknown>
): boolean {
  if (!definition.dependsOn) {
    return true;
  }

  const dependentValue = values[definition.dependsOn.parameter];
  return dependentValue === definition.dependsOn.value;
}

/**
 * Gets the default value for a parameter
 */
function getParameterDefault(definition: ParameterDefinition): unknown {
  if (definition.defaultValue !== undefined) {
    return definition.defaultValue;
  }

  if (definition.type === 'number' && definition.range) {
    return definition.range.default;
  }

  if (definition.type === 'enum' && definition.options && definition.options.length > 0) {
    return definition.options[0].value;
  }

  if (definition.type === 'boolean') {
    return false;
  }

  return undefined;
}

/**
 * Creates default values object from parameter schema
 */
function createDefaultValues(
  schema: ParameterDefinition[]
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const param of schema) {
    const defaultValue = getParameterDefault(param);
    if (defaultValue !== undefined) {
      defaults[param.name] = defaultValue;
    }
  }

  return defaults;
}

export interface ParameterValues {
  [key: string]: unknown;
}

export interface EntityParameterPanelProps {
  /** The parameter schema from the model definition */
  parameterSchema: ParameterDefinition[];
  /** Current parameter values */
  values: ParameterValues;
  /** Called when a parameter value changes */
  onChange: (values: ParameterValues) => void;
  /** Whether controls should be disabled (e.g., during streaming) */
  disabled?: boolean;
  /** Optional title for the panel */
  title?: string;
  /** Whether to show a reset to defaults button */
  showReset?: boolean;
  /** Filter to only show certain categories */
  categories?: ParameterCategory[];
  /** Parameters to render at the top, right after the header */
  priorityParams?: string[];
  /** CSS class name */
  className?: string;
}

/**
 * Groups parameters by category for organized display
 */
function groupByCategory(
  params: ParameterDefinition[]
): Map<ParameterCategory, ParameterDefinition[]> {
  const groups = new Map<ParameterCategory, ParameterDefinition[]>();

  for (const param of params) {
    const existing = groups.get(param.category) || [];
    groups.set(param.category, [...existing, param]);
  }

  return groups;
}

/**
 * Category display configuration
 */
const CATEGORY_CONFIG: Record<ParameterCategory, { label: string; order: number }> = {
  output: { label: 'Output', order: 1 },
  reasoning: { label: 'Reasoning', order: 2 },
  behavior: { label: 'Behavior', order: 3 },
  advanced: { label: 'Advanced', order: 4 },
};

/**
 * Renders an individual parameter control based on its definition
 */
function ParameterControl({
  definition,
  value,
  onChange,
  disabled = false,
}: {
  definition: ParameterDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
}) {
  const controlId = `param-${definition.name}`;

  switch (definition.control) {
    case 'slider': {
      if (!definition.range) {
        return null;
      }
      const numValue = typeof value === 'number' ? value : definition.range.default;
      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor={controlId} className="text-sm text-muted-foreground">
              {definition.label}
            </Label>
            <span className="text-sm text-muted-foreground">{numValue}</span>
          </div>
          <Slider
            id={controlId}
            min={definition.range.min}
            max={definition.range.max}
            step={definition.range.step || 1}
            value={[numValue]}
            onValueChange={(values) => onChange(values[0])}
            disabled={disabled}
          />
          {definition.description && (
            <p className="text-xs text-muted-foreground">{definition.description}</p>
          )}
        </div>
      );
    }

    case 'input': {
      if (definition.type === 'number') {
        const numValue = typeof value === 'number' ? value : (definition.range?.default ?? 0);
        return (
          <div className="space-y-2">
            <Label htmlFor={controlId} className="text-sm text-muted-foreground">
              {definition.label}
            </Label>
            <Input
              id={controlId}
              type="number"
              value={numValue || ''}
              onChange={(e) => {
                const val = e.target.value;
                onChange(val === '' ? 0 : parseInt(val, 10) || 0);
              }}
              onBlur={(e) => {
                const val = parseInt(e.target.value, 10);
                if (definition.range) {
                  const clamped = Math.max(
                    definition.range.min,
                    Math.min(definition.range.max, val || definition.range.default)
                  );
                  onChange(clamped);
                } else {
                  onChange(val || 0);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                }
              }}
              disabled={disabled}
              min={definition.range?.min}
              max={definition.range?.max}
            />
            {definition.description && (
              <p className="text-xs text-muted-foreground">{definition.description}</p>
            )}
          </div>
        );
      }
      // String input
      const strValue = typeof value === 'string' ? value : '';
      return (
        <div className="space-y-2">
          <Label htmlFor={controlId} className="text-sm text-muted-foreground">
            {definition.label}
          </Label>
          <Input
            id={controlId}
            type="text"
            value={strValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {definition.description && (
            <p className="text-xs text-muted-foreground">{definition.description}</p>
          )}
        </div>
      );
    }

    case 'select': {
      if (!definition.options || definition.options.length === 0) {
        return null;
      }
      const selectValue = typeof value === 'string' ? value : definition.options[0].value;
      return (
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">{definition.label}</Label>
          <Select
            value={selectValue}
            onValueChange={onChange}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {definition.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {definition.description && (
            <p className="text-xs text-muted-foreground">{definition.description}</p>
          )}
        </div>
      );
    }

    case 'checkbox':
    case 'toggle': {
      const boolValue = typeof value === 'boolean' ? value : (definition.defaultValue === true);
      return (
        <div className="flex items-start gap-3">
          <Checkbox
            id={controlId}
            checked={boolValue}
            onCheckedChange={(checked) => onChange(checked === true)}
            disabled={disabled}
          />
          <div className="space-y-1">
            <Label htmlFor={controlId} className="text-sm cursor-pointer">
              {definition.label}
            </Label>
            {definition.description && (
              <p className="text-xs text-muted-foreground">{definition.description}</p>
            )}
          </div>
        </div>
      );
    }

    case 'textarea': {
      const textValue = typeof value === 'string' ? value : '';
      return (
        <div className="space-y-2">
          <Label htmlFor={controlId} className="text-sm text-muted-foreground">
            {definition.label}
          </Label>
          <Textarea
            id={controlId}
            value={textValue}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            rows={4}
          />
          {definition.description && (
            <p className="text-xs text-muted-foreground">{definition.description}</p>
          )}
        </div>
      );
    }

    default:
      return null;
  }
}

/**
 * EntityParameterPanel - Main component
 *
 * Renders a panel of parameter controls based on a ParameterDefinition schema.
 * Supports grouping by category, conditional visibility, and reset to defaults.
 */
export function EntityParameterPanel({
  parameterSchema,
  values,
  onChange,
  disabled = false,
  title,
  showReset = true,
  categories,
  priorityParams,
  className,
}: EntityParameterPanelProps) {
  // Filter parameters by category if specified
  const filteredParams = useMemo(() => {
    if (!categories || categories.length === 0) {
      return parameterSchema;
    }
    return parameterSchema.filter((p) => categories.includes(p.category));
  }, [parameterSchema, categories]);

  // Extract priority parameters (rendered at top) and remaining parameters
  const { priorityParamDefs, remainingParams } = useMemo(() => {
    if (!priorityParams || priorityParams.length === 0) {
      return { priorityParamDefs: [], remainingParams: filteredParams };
    }
    const prioritySet = new Set(priorityParams);
    return {
      priorityParamDefs: filteredParams.filter((p) => prioritySet.has(p.name)),
      remainingParams: filteredParams.filter((p) => !prioritySet.has(p.name)),
    };
  }, [filteredParams, priorityParams]);

  // Group remaining parameters by category (excludes priority params)
  const groupedParams = useMemo(() => groupByCategory(remainingParams), [remainingParams]);

  // Sort categories by configured order
  const sortedCategories = useMemo(() => {
    return Array.from(groupedParams.keys()).sort(
      (a, b) => CATEGORY_CONFIG[a].order - CATEGORY_CONFIG[b].order
    );
  }, [groupedParams]);

  // Handle value change for a single parameter
  // Also applies conditional defaults for parameters that depend on the changed parameter
  const handleChange = useCallback(
    (paramName: string, value: unknown) => {
      const newValues: ParameterValues = { ...values, [paramName]: value };

      // Check if any parameters have conditional defaults that depend on this parameter
      for (const param of filteredParams) {
        if (param.conditionalDefault?.dependsOn === paramName) {
          // Get the conditional default value for the new value
          const valueKey = String(value);
          const conditionalValue = param.conditionalDefault.values[valueKey];
          if (conditionalValue !== undefined) {
            newValues[param.name] = conditionalValue;
          }
        }
      }

      onChange(newValues);
    },
    [values, onChange, filteredParams]
  );

  // Reset to defaults
  const handleReset = useCallback(() => {
    const defaults = createDefaultValues(filteredParams);
    onChange({ ...values, ...defaults });
  }, [filteredParams, values, onChange]);

  if (filteredParams.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {/* Header with title and reset button */}
      {(title || showReset) && (
        <div className="flex items-center justify-between mb-4">
          {title && <Label className="text-sm font-medium">{title}</Label>}
          {showReset && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={disabled}
              className="h-8 text-sm"
            >
              Defaults
            </Button>
          )}
        </div>
      )}

      {/* Priority parameters (rendered at top, before category groups) */}
      {priorityParamDefs.length > 0 && (
        <div className="space-y-4 mb-4">
          {priorityParamDefs
            .filter((p) => isParameterVisible(p, values))
            .map((param) => (
              <ParameterControl
                key={param.name}
                definition={param}
                value={values[param.name]}
                onChange={(value) => handleChange(param.name, value)}
                disabled={disabled}
              />
            ))}
        </div>
      )}

      {/* Parameter groups */}
      <div className="space-y-6">
        {sortedCategories.map((category) => {
          const params = groupedParams.get(category) || [];
          const visibleParams = params.filter((p) => isParameterVisible(p, values));

          if (visibleParams.length === 0) {
            return null;
          }

          return (
            <div key={category} className="space-y-4">
              {/* Category header (only show if multiple categories) */}
              {sortedCategories.length > 1 && (
                <div className="border-t border-border pt-4">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {CATEGORY_CONFIG[category].label}
                  </Label>
                </div>
              )}

              {/* Parameters in this category */}
              {visibleParams.map((param) => (
                <ParameterControl
                  key={param.name}
                  definition={param}
                  value={values[param.name]}
                  onChange={(value) => handleChange(param.name, value)}
                  disabled={disabled}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Hook for managing parameter values with a model definition schema
 *
 * Initializes values with defaults from the schema and provides
 * helper functions for common operations.
 */
export function useParameterValues(
  parameterSchema: ParameterDefinition[],
  initialValues?: ParameterValues
) {
  const [values, setValues] = React.useState<ParameterValues>(() => {
    const defaults = createDefaultValues(parameterSchema);
    return { ...defaults, ...initialValues };
  });

  // Update values when schema changes
  React.useEffect(() => {
    const defaults = createDefaultValues(parameterSchema);
    setValues((prev) => ({
      ...defaults,
      ...prev,
    }));
  }, [parameterSchema]);

  const updateValue = useCallback((name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const resetToDefaults = useCallback(() => {
    setValues(createDefaultValues(parameterSchema));
  }, [parameterSchema]);

  return {
    values,
    setValues,
    updateValue,
    resetToDefaults,
  };
}

export default EntityParameterPanel;
